import React, {CSSProperties, useEffect, useState} from "react";
import { Checkbox } from "baseui/checkbox";
import { colors, fonts, glyphs } from "../../Styles";

// #region | CheckStyles
/* eslint-disable no-unused-vars */
export enum CheckStyles {
  transparent = "transparent",
  grey = "grey",
  blue = "blue"
}
/* eslint-enable no-unused-vars */

const createCheckStyle = (checkStyle?: CheckStyles | string ) => {
  let colorStyle = ""
  let borderStyle = ""
  let borderRadius = ""
  let size = ""
  let textStyle
  let checkMarkColor: colors

  switch (checkStyle) {
    case CheckStyles.transparent:
      colorStyle = colors.transparent
      borderStyle = "2px solid"+colors.greyLightTertiary
      borderRadius = "5px"
      size = "22px"
      textStyle = {color: "none", ...fonts.fontSize3}
      checkMarkColor = colors.systemGreen
      break
    case CheckStyles.grey:
      colorStyle = colors.transparent
      borderStyle = "2px solid"+colors.greyLightSecondary
      borderRadius = "5px"
      size = "22px"
      textStyle = {color: colors.greyDarkPrimary, ...fonts.fontSize4}
      checkMarkColor = colors.systemGreen
      break
    case CheckStyles.blue:
      default:
      colorStyle = colors.blueDarkSecondary
      borderStyle = "none"
      borderRadius = "10px"
      size = "30px"
      textStyle = {color: colors.greyLightTertiary, ...fonts.fontSize3}
      checkMarkColor = colors.greyLightTertiary
      break
  }

  return {colorStyle, borderStyle, borderRadius, size, textStyle, checkMarkColor}
}
// #endregion

// #region | CheckBox
interface Check_Props {
  text?: string
  isChecked?: boolean;
  disabled?:boolean;
  onChange?: (e:boolean) => void;
  inTable?: boolean;
  checkStyle?: CheckStyles
}
/**
 * CheckBox
 * @param text - The text displayed next to the checkbox.
 * @param isChecked - Indicates whether the checkbox is checked.
 * @param disabled - Disables the checkbox if `true`.
 * @param onChange - Callback function triggered when the value changes.
 * @param inTable - Adjusts the margin if the checkbox is inside a table.
 * @param checkStyle - Defines the style of the checkbox (e.g., color and size).
 * @returns A styled checkbox with optional text.
 */
export default function Check({ text, isChecked, disabled=false, onChange, inTable, checkStyle }:Check_Props) {
  const [checked, setChecked] = useState(isChecked || false);

  const { colorStyle, borderStyle, borderRadius, size, textStyle, checkMarkColor } = createCheckStyle(checkStyle);

  const _root = text
  ? { display: "flex", flexDirection: "row" as const, alignItems: "center", justifyContent: "start", gap: "10px"}
  : undefined

  useEffect(() => {
    setChecked(isChecked || false);
  }, [isChecked]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newChecked = e.target.checked;
    setChecked(newChecked);
    if (onChange) {
      onChange(newChecked);
    }
  };

  return (
    <div style={{..._root, }}> 
      <div style={{
        position: "relative",
        width: size,
        height: size,
        backgroundColor: colorStyle,
        border: borderStyle,
        borderRadius: borderRadius,
        margin: inTable ? "-3px 0px 0px -3px" : "0px"
      }}>
        <Checkbox 
          disabled={disabled}
          checked={checked}
          onChange={handleChange}
          overrides={{
            Checkmark: { style: () => Object.assign({}, {
                width: size,
                height: size,
                backgroundImage: "none",
                backgroundColor: colors.transparent,
                border: "none",
                zIndex: "10",
            } as CSSProperties)} 
          }} 
        />
        {!checked 
        ? <></>
        : <div style={{ zIndex: "5", position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "20px", height: "20px"}}>
            <glyphs.Check fillColor={checkMarkColor}/>
          </div>
        }
      </div>
      <p style={{...fonts.paragraph, ...textStyle}}>{text}</p>
    </div>
  );
}
// #endregion

// #region | CheckBox List
interface CheckList_Props {
  text?: string[]
  isChecked?: boolean[]
  disabled?:boolean[]
  onChange?: ((e:boolean) => void)[]
  inTable?: boolean
  checkStyle?: CheckStyles
  isHorizontal?: boolean
}
/**
 * CheckBox List
 * @param text - A list of texts displayed next to each checkbox.
 * @param isChecked - A list of booleans indicating the checked status of each checkbox.
 * @param disabled - A list of booleans indicating whether each checkbox is disabled.
 * @param onChange - A list of callback functions triggered by changes in each checkbox.
 * @param inTable - Indicates if the checkboxes are inside a table, for margin adjustment.
 * @param checkStyle - Style applied to all checkboxes (e.g., color and size).
 * @param isHorizontal - Determines whether the list is displayed horizontally or vertically.
 * @returns A list of styled checkboxes.
 */
export function CheckList ({text = [], isChecked = [], onChange = [], disabled = [], inTable = false, checkStyle, isHorizontal}:CheckList_Props) {
  return(
    <div style={{display: "flex", flexDirection: isHorizontal?  "row" : "column", alignItems: "center", justifyContent: "start", gap: "20px"}}>
      {text.map((text, index) => (
        <Check
          key={index}
          text={text}
          isChecked={isChecked[index]}
          disabled={disabled[index]}
          onChange={onChange ? onChange[index] : undefined}
          inTable={inTable}
          checkStyle={checkStyle}
        />
      ))}
    </div>
  )
}
// #endregion

